// Copyright (c) 2015-present Mattermost, Inc. All Rights Reserved.
// See LICENSE.txt for license information.

import classNames from 'classnames';
import React from 'react';
import type {ReactNode} from 'react';
import {FormattedMessage} from 'react-intl';

import SaveButton from 'components/save_button';

import Constants from 'utils/constants';
import {isKeyPressed} from 'utils/keyboard';
import {a11yFocus} from 'utils/utils';

type Props = {

    // Array of inputs selection
    inputs?: ReactNode;
    containerStyle?: string;
    serverError?: ReactNode;

    /**
     * Settings extra information
     */
    extraInfo?: ReactNode;

    /**
     * Info position
     */
    infoPosition?: string;

    /**
     * Settings or tab section
     */
    section: string;
    updateSection?: (section: string) => void;
    setting?: string;
    submit?: ((setting?: string) => void) | null;
    disableEnterSubmit?: boolean;
    submitExtra?: ReactNode;
    saving?: boolean;
    title?: ReactNode;
    extraContentBeforeSettingList?: ReactNode;
    isFullWidth?: boolean;
    cancelButtonText?: ReactNode;
    shiftEnter?: boolean;
    saveButtonText?: string;
    saveButtonClassName?: string;
    isValid?: boolean;
}
export default class SettingItemMax extends React.PureComponent<Props> {
    settingList: React.RefObject<HTMLDivElement>;

    static defaultProps = {
        infoPosition: 'bottom',
        saving: false,
        section: '',
        containerStyle: '',
    };

    constructor(props: Props) {
        super(props);
        this.settingList = React.createRef();
    }

    componentDidMount() {
        if (this.settingList.current) {
            const focusableElements: NodeListOf<HTMLElement> = this.settingList.current.querySelectorAll('.btn:not(.save-button):not(.btn-tertiary), input.form-control, input[type="radio"][checked], input[type="checkbox"], select, textarea, [tabindex]:not([tabindex="-1"])');
            if (focusableElements.length > 0) {
                a11yFocus(focusableElements[0]);
            } else {
                a11yFocus(this.settingList.current);
            }
        }

        document.addEventListener('keydown', this.onKeyDown);
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.onKeyDown);
    }

    onKeyDown = (e: KeyboardEvent) => {
        const target = e.target as HTMLElement;
        if (this.props.shiftEnter && isKeyPressed(e, Constants.KeyCodes.ENTER) && e.shiftKey) {
            return;
        }
        if (this.props.disableEnterSubmit !== true &&
            isKeyPressed(e, Constants.KeyCodes.ENTER) &&
            this.props.submit &&
            target.tagName !== 'SELECT' &&
            target.parentElement &&
            target.parentElement.className !== 'react-select__input' &&
            !target.classList.contains('btn-tertiary') &&
            this.settingList.current &&
            this.settingList.current.contains(target)) {
            this.handleSubmit(e);
        }
    };

    handleSubmit = (e: React.MouseEvent | KeyboardEvent) => {
        e.preventDefault();

        if (this.props.setting && this.props.submit) {
            this.props.submit(this.props.setting);
        } else if (this.props.submit) {
            this.props.submit();
        }
    };

    handleUpdateSection = (e: React.MouseEvent) => {
        if (this.props.updateSection) {
            this.props.updateSection(this.props.section);
        }
        e.preventDefault();
    };

    render() {
        let serverError = null;
        if (this.props.serverError) {
            serverError = (
                <div className='form-group'>
                    <label
                        className='col-sm-12 has-error'
                    >
                        <i
                            className='icon icon-alert-circle-outline'
                            role='presentation'
                        />
                        <span className='sr-only'>
                            <FormattedMessage
                                id='setting_item_max.error'
                                defaultMessage='Error'
                            />
                        </span>
                        <span id='serverError'>
                            {this.props.serverError}
                        </span>
                    </label>
                </div>
            );
        }

        let extraInfo = null;
        let hintClass = 'setting-list__hint';
        if (this.props.infoPosition === 'top') {
            hintClass = 'pb-3';
        }

        if (this.props.extraInfo) {
            extraInfo = (
                <div
                    id='extraInfo'
                    className={hintClass}
                >
                    {this.props.extraInfo}
                </div>
            );
        }

        let submit: JSX.Element | null = null;
        if (this.props.submit) {
            submit = (
                <SaveButton
                    defaultMessage={this.props.saveButtonText}
                    saving={this.props.saving}
                    disabled={this.props.saving || this.props.isValid === false}
                    onClick={this.handleSubmit}
                    btnClass={this.props.saveButtonClassName}
                />
            );
        }

        const inputs = this.props.inputs;

        let title;
        if (this.props.title) {
            title = (
                <h4
                    id='settingTitle'
                    className='col-sm-12 section-title'
                >
                    {this.props.title}
                </h4>
            );
        }

        let listContent = (
            <div className='setting-list-item'>
                {inputs}
                {extraInfo}
            </div>
        );

        if (this.props.infoPosition === 'top') {
            listContent = (
                <div>
                    {extraInfo}
                    {inputs}
                </div>
            );
        }

        let cancelButtonText;
        if (this.props.cancelButtonText) {
            cancelButtonText = this.props.cancelButtonText;
        } else {
            cancelButtonText = (
                <FormattedMessage
                    id='setting_item_max.cancel'
                    defaultMessage='Cancel'
                />
            );
        }

        return (
            <section
                className={`section-max form-horizontal ${this.props.containerStyle}`}
            >
                {title}
                {this.props.extraContentBeforeSettingList}
                <div
                    className={classNames('sectionContent', {
                        'col-sm-12': this.props.isFullWidth,
                        'col-sm-10 col-sm-offset-2': !this.props.isFullWidth,
                    })}
                >
                    <div
                        tabIndex={-1}
                        ref={this.settingList}
                        className='setting-list'
                    >
                        {listContent}
                        <div className='setting-list-item'>
                            <hr/>
                            {this.props.submitExtra}
                            <div
                                role='alert'
                            >
                                {serverError}
                            </div>
                            {submit}
                            <button
                                id={'cancelSetting'}
                                className='btn btn-tertiary'
                                onClick={this.handleUpdateSection}
                            >
                                {cancelButtonText}
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        );
    }
}
